// apps/admin/src/i18n/index.ts
//
// Phase 8.3 Wave 8 Task 68 — i18n barrel + lazy-loader for the Phase 8.3
// admin namespace (`p83`). Loads EN/VI/FR chunks per-route so initial admin
// bundle stays under the <300KB gzipped budget (acceptance).
//
// vg-binding: CONTEXT:decisions=P8.D-165,P8.D-390
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: P8.D-165 — Admin i18n EN/VI/FR with `admin.{module}.{page}.{element}`
//             namespace pattern. This module extends that to Phase 8.3 modules
//             via the dedicated `p83` namespace (reports/system/monitoring/
//             inventory/cms subtrees) so existing admin/navigation namespaces
//             are not bloated.
// vg-binding: P8.D-390 — CMS content per-language tabs. The `p83.cms.common.languageTabs`
//             keys feed the EN/VI/FR tab strip used by every CMS editor.
// vg-binding: INTERFACE-STANDARDS:error-shape — `p83.errors.envelope.*` keys
//             render the API error envelope ({ ok: false, error: { code, message } })
//             with human copy per locale; admin pages map `error.code` →
//             `p83:errors.envelope.<key>` so the error shape stays canonical.
//
// Design notes:
//   * `config.ts` initialises the i18next instance with `common`, `errors`,
//     `admin`, `navigation` namespaces only — those must render synchronously
//     for the shell + error pages.
//   * THIS module adds `p83` on demand. Routes call `loadP83Namespace()` once
//     (typically from the lazy route's component); subsequent calls are
//     no-ops because `i18n.hasResourceBundle()` short-circuits.
//   * `ensureP83Loaded()` returns a Promise so routes can wrap their page in
//     `<Suspense>` + a tiny `await` boundary if they need to block render
//     until copy is ready (otherwise i18next renders the key string until
//     the bundle arrives — acceptable for above-the-fold).
//
// Bundle behaviour:
//   * `import('./locales/<lng>/p83.json')` is a dynamic import → Vite splits
//     each locale into its own chunk (`p83-en-<hash>.json`, etc.).
//   * `loadP83Namespace('en')` only fetches the EN chunk; switching locale
//     later triggers another chunk fetch through `loadP83Namespace('vi')`.

import { i18n, type SupportedLocale } from './config.js';

export {
  ADMIN_DATE_FORMAT,
  ADMIN_DEFAULT_LOCALE,
  ADMIN_SUPPORTED_LOCALES,
  changeAdminLocale,
  detectInitialLocale,
  i18n,
  initAdminI18n,
} from './config.js';
export { useTranslation } from './useTranslation.js';
export type { SupportedLocale } from './config.js';

/** Phase 8.3 namespace identifier — single source of truth. */
export const P83_NAMESPACE = 'p83' as const;

/** Track in-flight loads so concurrent route mounts share one fetch. */
const inflight = new Map<SupportedLocale, Promise<void>>();

/**
 * Dynamically import the `p83` resource bundle for a single locale and
 * register it with the live i18next instance. Idempotent: returns the
 * existing promise when called twice for the same locale, and resolves
 * immediately when the bundle is already registered.
 *
 * Routes call this from a Suspense-aware boundary (or fire-and-forget when
 * graceful-degradation to the raw key is acceptable).
 */
export async function loadP83Namespace(
  locale: SupportedLocale,
): Promise<void> {
  // Already registered — nothing to do.
  if (i18n.hasResourceBundle(locale, P83_NAMESPACE)) {
    return;
  }

  // De-dupe concurrent loaders for the same locale.
  const existing = inflight.get(locale);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    let resource: Record<string, unknown>;
    // NO `with: { type: 'json' }` attribute here (GAP-3, 2026-07-06):
    // rolldown-vite 8 emits the p83 chunk but leaves the dynamic import
    // UNREWRITTEN when the import carries an attribute clause — the
    // production bundle then requests the raw `./locales/<lng>/p83.json`
    // path, Nginx SPA-fallbacks it to index.html, and the browser throws
    // "Expected a JSON module script but got text/html" in a suspense
    // retry loop (every ensureP83Loaded route stuck on "Loading…").
    // Vite transforms attribute-less JSON imports natively; the attribute
    // is only needed for raw browser ESM, which never sees this code.
    switch (locale) {
      case 'en':
        resource = (await import('./locales/en/p83.json'))
          .default as Record<string, unknown>;
        break;
      case 'vi':
        resource = (await import('./locales/vi/p83.json'))
          .default as Record<string, unknown>;
        break;
      case 'fr':
        resource = (await import('./locales/fr/p83.json'))
          .default as Record<string, unknown>;
        break;
      default: {
        // Exhaustiveness guard — TS will flag missing locales here.
        const _unreachable: never = locale;
        throw new Error(`Unsupported locale: ${String(_unreachable)}`);
      }
    }

    // `deep=true` so nested keys (e.g. `cms.homepage.tabs.hero`) merge
    // correctly when an earlier route already loaded a partial bundle.
    // `overwrite=false` so first-write-wins per locale (HMR safety).
    i18n.addResourceBundle(
      locale,
      P83_NAMESPACE,
      resource,
      /* deep */ true,
      /* overwrite */ false,
    );
  })().finally(() => {
    inflight.delete(locale);
  });

  inflight.set(locale, promise);
  return promise;
}

/**
 * Convenience wrapper: load the p83 bundle for the active locale AND the
 * fallback (EN) so missing keys gracefully fall through. Routes that
 * `Suspense`-await this get a fully-populated namespace on first render.
 *
 * Resolves with `void`; failures bubble up so the Suspense boundary can
 * surface a retry UI.
 */
export async function ensureP83Loaded(active: SupportedLocale): Promise<void> {
  const targets: SupportedLocale[] = active === 'en' ? ['en'] : [active, 'en'];
  await Promise.all(targets.map((lng) => loadP83Namespace(lng)));
}
